import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import { Alert, Box, CircularProgress, Paper, Snackbar, Typography } from '@mui/material';

const GoogleDomainAuthContext = createContext(null);
const GOOGLE_INIT_STATE_KEY = '__GMER_GOOGLE_ID_INIT_STATE__';
// Display guidance only: the backend verifies Google credentials and enforces domain access.
const DEFAULT_ALLOWED_DOMAINS = ['dealerinspire.com', 'cars.com'];

const stringifyEnvBool = (value, fallback = false) => {
  if (value === undefined || value === null) return fallback;
  return String(value).toLowerCase() === 'true';
};

const defaultRoutes = {
  me: '/auth/me',
  login: '/auth/google',
  logout: '/auth/logout',
};

export const GoogleDomainAuthProvider = ({
  children,
  googleClientId = import.meta.env?.VITE_GOOGLE_CLIENT_ID ?? '',
  requireAuth = stringifyEnvBool(import.meta.env?.VITE_AUTH_REQUIRED, true),
  allowedDomain,
  allowedDomains = DEFAULT_ALLOWED_DOMAINS,
  unauthorizedMessage,
  authBasePath = '/api',
  routes = {},
  httpClient = axios,
}) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const mergedRoutes = useMemo(() => ({ ...defaultRoutes, ...routes }), [routes]);
  // Keep the legacy single-domain display override for existing provider consumers.
  const allowedAccountsLabel = (allowedDomain ? [allowedDomain] : allowedDomains)
    .map((domain) => `@${domain}`)
    .join(' or ');
  const resolvedUnauthorizedMessage =
    unauthorizedMessage || `Access is restricted to ${allowedAccountsLabel} accounts.`;

  useEffect(() => {
    if (httpClient?.defaults) {
      httpClient.defaults.withCredentials = true;
    }
  }, [httpClient]);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await httpClient.get(`${authBasePath}${mergedRoutes.me}`);
      setUser(data.user);
    } catch (_err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [authBasePath, httpClient, mergedRoutes.me]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const logout = useCallback(async () => {
    await httpClient.post(`${authBasePath}${mergedRoutes.logout}`);
    setUser(null);
  }, [authBasePath, httpClient, mergedRoutes.logout]);

  const contextValue = useMemo(
    () => ({
      user,
      setUser,
      loading,
      logout,
      config: {
        googleClientId,
        requireAuth,
        allowedDomain,
        allowedAccountsLabel,
        unauthorizedMessage: resolvedUnauthorizedMessage,
        authBasePath,
        routes: mergedRoutes,
      },
      httpClient,
    }),
    [
      allowedDomain,
      allowedAccountsLabel,
      authBasePath,
      googleClientId,
      httpClient,
      loading,
      logout,
      mergedRoutes,
      requireAuth,
      resolvedUnauthorizedMessage,
      user,
    ]
  );

  return (
    <GoogleDomainAuthContext.Provider value={contextValue}>
      {children}
    </GoogleDomainAuthContext.Provider>
  );
};

export const useGoogleDomainAuth = () => useContext(GoogleDomainAuthContext);
export const useAuth = useGoogleDomainAuth;

export const GoogleDomainSignInButton = ({ mode = 'standard', theme = 'outline', size = 'medium' }) => {
  const btnRef = React.useRef(null);
  const { setUser, config, httpClient } = useGoogleDomainAuth() || {};
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    if (!config?.googleClientId || !btnRef.current) return;

    const handleCredentialResponse = async (response) => {
      try {
        const idToken = response.credential;
        const { data } = await httpClient.post(
          `${config.authBasePath}${config.routes.login}`,
          { idToken }
        );
        setUser?.(data.user);
      } catch (error) {
        console.error('Google login failed', error);
        setLoginError(`Login failed. Use a verified ${config.allowedAccountsLabel} Google account.`);
      }
    };

    const googleAuth = window.google?.accounts?.id;
    if (!googleAuth) {
      console.warn(
        '[GoogleDomainAuth] window.google.accounts.id is not available. ' +
          'Ensure the Google Identity Services script is loaded in index.html.'
      );
      return;
    }

    const existingInitState = window[GOOGLE_INIT_STATE_KEY];
    if (existingInitState?.clientId !== config.googleClientId) {
      googleAuth.initialize({
        client_id: config.googleClientId,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window[GOOGLE_INIT_STATE_KEY] = {
        clientId: config.googleClientId,
      };
    }

    googleAuth.renderButton(btnRef.current, {
      theme,
      size,
      type: mode,
      text: 'signin_with',
    });
  }, [config, httpClient, setUser, mode, size, theme]);

  return (
    <>
      <div ref={btnRef} />
      <Snackbar
        open={Boolean(loginError)}
        autoHideDuration={6000}
        onClose={() => setLoginError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setLoginError('')} sx={{ width: '100%' }}>
          {loginError}
        </Alert>
      </Snackbar>
    </>
  );
};

export const GoogleDomainAuthGate = ({
  children,
  loadingFallback = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
      }}
    >
      <CircularProgress />
    </Box>
  ),
}) => {
  const context = useGoogleDomainAuth();

  if (!context) {
    console.warn('[GoogleDomainAuth] Gate rendered without provider.');
    return children;
  }

  const { user, loading, config } = context;

  if (!config.requireAuth) {
    return children;
  }

  if (loading) {
    return loadingFallback;
  }

  if (!user) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          p: 2,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 420, textAlign: 'center' }} elevation={3}>
          <Typography variant="h6" gutterBottom>
            Sign in to continue
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {config.unauthorizedMessage}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <GoogleDomainSignInButton />
          </Box>
        </Paper>
      </Box>
    );
  }

  return children;
};
